<script setup lang="ts">
import { cloneDeep } from 'lodash'
import { computed, ref, VNode } from 'vue'

import { getOppositeParticipant } from '@collector/shared-ui-domain'
import { HandballThrowBlockedAttribute } from '@collector/sportsapi-client-legacy'
import { useIncidentGroupComponents } from '@mobile/views/Relation/DetailsPanel/Modals/IncidentDetailsModal/composables/useIncidentGroupComponents'
import PlayerSelectGroup from '@mobile/views/Relation/DetailsPanel/Modals/IncidentDetailsModal/PlayerSelectGroup.vue'
import { useLineupsSelectOptions } from '@mobile/views/Relation/Shared/Lineups/useLineupsSelectOption'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'

import { DetailButtonsConfiguration } from '../../../composables/useSportOptions'
import { useThrowPositionDetailsModalForm } from '../components/ThrowPositionDetailsModal/composables'
import ThrowPositionDetailsModal from '../components/ThrowPositionDetailsModal/ThrowPositionDetailsModal.vue'
import { GroupName } from '../consts'
import {
  groups,
  pitchDistanceAttributeMap,
  pitchPositionAttributeMap,
} from './consts'

interface Props {
  linkedIncident: ProbableLinkedIncident
}

const props = defineProps<Props>()

// emits
const emit = defineEmits(['close'])

// data
const localLinkedIncident = cloneDeep(props.linkedIncident)

// composables
const { probableHomeParticipant, probableAwayParticipant } =
  useEventInjections()
const { findParticipant } = useLineupsSelectOptions(
  ref(localLinkedIncident.incident),
)

// data
const participant = localLinkedIncident.participant!
const oppositeParticipant = getOppositeParticipant(
  participant.id,
  probableHomeParticipant.value,
  probableAwayParticipant.value,
)

// composables
const incidentGroupComponentsComposable = useIncidentGroupComponents(
  localLinkedIncident,
  groups,
)
const {
  updateIncident: _updateIncident,
  removeAttribute,
  addAttribute,
  addGroupAttribute,
} = incidentGroupComponentsComposable
const { assistant, pitchDistance, pitchPosition } =
  useThrowPositionDetailsModalForm(
    localLinkedIncident,
    removeAttribute,
    addAttribute,
    pitchDistanceAttributeMap,
    pitchPositionAttributeMap,
    undefined,
    () => {
      const styleGroup = groups.get(
        GroupName.Style,
      ) as DetailButtonsConfiguration<HandballThrowBlockedAttribute>

      addGroupAttribute(styleGroup, HandballThrowBlockedAttribute.Air)
    },
  )

// computed
const isThrowPositionFulfilled = computed(() => {
  return !!(pitchDistance.value && pitchPosition.value)
})

// methods
function updateIncident(): void {
  const assistant_id = assistant.value
  const assistant_name = findParticipant(assistant.value)?.name

  _updateIncident({ assistant_id, assistant_name })

  emit('close')
}

function getGroupComponent(name: GroupName): VNode | null {
  return incidentGroupComponentsComposable.getGroupComponent(groups.get(name)!)
}
</script>

<template>
  <ThrowPositionDetailsModal
    v-model:pitchDistance="pitchDistance"
    v-model:pitchPosition="pitchPosition"
    :linkedIncident="localLinkedIncident"
    :isThrowPositionFulfilled
    pitchPositionName="Throw position"
    goalPositionDisabled
    @close="$emit('close')"
    @save="updateIncident"
  >
    <template #prePitchComponent>
      <div class="space-y-4">
        <PlayerSelectGroup
          v-model="assistant"
          important
          name="Blocked by"
          nullable
          :isFulfilled="!!assistant"
          :participant="oppositeParticipant"
        />
      </div>
    </template>

    <component :is="getGroupComponent(GroupName.Style)" />
    <component :is="getGroupComponent(GroupName.Type)" />
  </ThrowPositionDetailsModal>
</template>
